import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Card, Divider, Grid, IconButton,
    List, ListItem, ListItemIcon, ListItemText,
    MenuItem, TextField, Typography, Alert, Stack,
    CircularProgress, Tooltip, LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import BusinessIcon from "@mui/icons-material/Business";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotesIcon from "@mui/icons-material/Notes";

import { useAuditUser } from "../hooks/useAuditUser";
import { getSucursales, getControladores, getCategorias, getAdvertencias, crearItem } from "../services/audit.service";

// ── Helpers ───────────────────────────────────────────────────
const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const FORM_INIT = {
    titulo: "",
    descripcion: "",
    categoria: "",
    advertencia_id: "",
    prioridad: "MEDIA",
    sucursal_id: "",
    controlador_username: "",
    fecha_deteccion: new Date().toISOString().split("T")[0],
    fecha_requerida: "",
};

// ── Sección header ────────────────────────────────────────────
function SectionHeader({ icon, label }) {
    return (
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <Box sx={{ color: "primary.main", display: "flex" }}>
                {React.cloneElement(icon, { sx: { fontSize: 16 } })}
            </Box>
            <Typography variant="overline">{label}</Typography>
        </Stack>
    );
}

// ══════════════════════════════════════════════════════════════
export default function AuditNew() {
    const navigate = useNavigate();
    const { username, puedeCrear } = useAuditUser();

    const [form, setForm] = useState(FORM_INIT);
    const [archivos, setArchivos] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [controladores, setControladores] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [advertencias, setAdvertencias] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getSucursales().then(setSucursales).catch(() => setError("No se pudieron cargar las sucursales."));
        getControladores().then(setControladores).catch(() => { });
        getCategorias().then(setCategorias).catch(() => { });
        getAdvertencias().then(setAdvertencias).catch(() => { });
    }, []);

    if (!puedeCrear) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">No tenés permisos para crear ítems de auditoría.</Alert>
            </Box>
        );
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: "" }));
    };

    const handleArchivos = (e) => {
        const nuevos = Array.from(e.target.files || []);
        setArchivos(prev => {
            const ex = new Set(prev.map(f => `${f.name}-${f.size}`));
            return [...prev, ...nuevos.filter(f => !ex.has(`${f.name}-${f.size}`))];
        });
        e.target.value = "";
    };

    const validar = () => {
        const e = {};
        if (!form.titulo.trim()) e.titulo = "Obligatorio";
        if (!form.descripcion.trim()) e.descripcion = "Obligatorio";
        if (!form.sucursal_id) e.sucursal_id = "Seleccioná una sucursal";
        if (!form.fecha_deteccion) e.fecha_deteccion = "Obligatorio";
        if (!form.fecha_requerida) e.fecha_requerida = "Obligatorio";
        if (form.fecha_requerida && form.fecha_deteccion &&
            form.fecha_requerida < form.fecha_deteccion)
            e.fecha_requerida = "No puede ser anterior a la detección";
        if (!form.categoria) e.categoria = "Seleccioná una categoría";
        if (!form.advertencia_id) e.advertencia_id = "Seleccioná un tipo de advertencia";
        setErrors(e);
        return !Object.keys(e).length;
    };

    const handleSubmit = async () => {
        if (!validar()) return;
        setGuardando(true);
        setError(null);
        try {
            const { id } = await crearItem(
                { ...form, auditor_username: username, controlador_username: form.controlador_username || undefined },
                archivos
            );
            navigate(`../${id}`, { replace: true });
        } catch (e) {
            setError(e?.response?.data?.error || "Error al guardar. Intentá nuevamente.");
        } finally {
            setGuardando(false);
        }
    };

    // ── Render ────────────────────────────────────────────────
    return (
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, backgroundColor: "background.default", minHeight: "100vh" }}>

            {/* ── Header ── */}
            <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <IconButton onClick={() => navigate(-1)} size="small">
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box>
                    <Typography variant="h5">Nuevo ítem</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Registrá un nuevo hallazgo para investigación
                    </Typography>
                </Box>
            </Stack>

            {guardando && <LinearProgress sx={{ mb: 1.5 }} />}
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

            <Grid container spacing={2}>

                {/* ══ COL IZQUIERDA ══ */}
                <Grid item xs={12} md={8}>

                    {/* Identificación */}
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionHeader icon={<NotesIcon />} label="Identificación del hallazgo" />
                            <Stack gap={1.5}>
                                <TextField
                                    fullWidth label="Título *" name="titulo"
                                    value={form.titulo} onChange={handleChange}
                                    error={!!errors.titulo} helperText={errors.titulo}
                                    placeholder="Descripción breve del hallazgo"
                                    inputProps={{ maxLength: 200 }}
                                />
                                <TextField
                                    fullWidth multiline rows={4}
                                    label="Descripción / Observación *" name="descripcion"
                                    value={form.descripcion} onChange={handleChange}
                                    error={!!errors.descripcion} helperText={errors.descripcion}
                                    placeholder="Detallá el hallazgo, contexto e impacto..."
                                />
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select fullWidth label="Categoría *" name="categoria"
                                            value={form.categoria} onChange={handleChange}
                                            error={!!errors.categoria} helperText={errors.categoria}
                                        >
                                            {categorias.map(c => (
                                                <MenuItem key={c.id} value={c.nombre}>{c.nombre}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select fullWidth label="Tipo de advertencia *" name="advertencia_id"
                                            value={form.advertencia_id} onChange={handleChange}
                                            error={!!errors.advertencia_id} helperText={errors.advertencia_id}
                                        >
                                            {advertencias.map(a => (
                                                <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField select fullWidth label="Prioridad *" name="prioridad"
                                            value={form.prioridad} onChange={handleChange}>
                                            <MenuItem value="BAJA">🟢  Baja</MenuItem>
                                            <MenuItem value="MEDIA">🟡  Media</MenuItem>
                                            <MenuItem value="ALTA">🟠  Alta</MenuItem>
                                            <MenuItem value="CRITICA">🔴  Crítica</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Box>
                    </Card>

                    {/* Adjuntos */}
                    <Card>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionHeader icon={<AttachFileIcon />} label="Adjuntos" />
                            <Button
                                component="label" variant="outlined"
                                startIcon={<AttachFileIcon />}
                                disabled={archivos.length >= 10}
                            >
                                Seleccionar archivos
                                <input type="file" multiple hidden onChange={handleArchivos} />
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                Máx. 10 archivos · 20 MB c/u
                            </Typography>

                            {archivos.length > 0 && (
                                <List dense sx={{ mt: 1 }}>
                                    {archivos.map((f, idx) => (
                                        <ListItem
                                            key={idx} disableGutters
                                            sx={{ borderBottom: "1px solid", borderColor: "divider", py: 0.5 }}
                                            secondaryAction={
                                                <Tooltip title="Quitar">
                                                    <IconButton size="small" onClick={() =>
                                                        setArchivos(p => p.filter((_, i) => i !== idx))}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                        >
                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                <InsertDriveFileIcon fontSize="small" color="action" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Typography variant="body2" noWrap sx={{ maxWidth: 340 }}>{f.name}</Typography>}
                                                secondary={fmtSize(f.size)}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            {!archivos.length && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                                    Ningún archivo seleccionado
                                </Typography>
                            )}
                        </Box>
                    </Card>
                </Grid>

                {/* ══ COL DERECHA ══ */}
                <Grid item xs={12} md={4}>

                    {/* Ubicación y fechas */}
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionHeader icon={<BusinessIcon />} label="Ubicación y fechas" />
                            <Stack gap={1.5}>
                                <TextField select fullWidth label="Sucursal *" name="sucursal_id"
                                    value={form.sucursal_id} onChange={handleChange}
                                    error={!!errors.sucursal_id} helperText={errors.sucursal_id}>
                                    <MenuItem value="">— Seleccioná —</MenuItem>
                                    {sucursales.map(s => (
                                        <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth type="date"
                                    label="Fecha detección *" name="fecha_deteccion"
                                    value={form.fecha_deteccion} onChange={handleChange}
                                    error={!!errors.fecha_deteccion} helperText={errors.fecha_deteccion}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    fullWidth type="date"
                                    label="Fecha límite de solución *" name="fecha_requerida"
                                    value={form.fecha_requerida} onChange={handleChange}
                                    error={!!errors.fecha_requerida} helperText={errors.fecha_requerida}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: form.fecha_deteccion }}
                                />
                            </Stack>
                        </Box>
                    </Card>

                    {/* Asignación */}
                    <Card sx={{ mb: 2 }}>
                        <Box sx={{ p: "12px 16px" }}>
                            <SectionHeader icon={<PersonOutlineIcon />} label="Asignación" />
                            <Stack gap={1.5}>
                                <TextField select fullWidth label="Asignar a controlador"
                                    name="controlador_username"
                                    value={form.controlador_username} onChange={handleChange}
                                    helperText="Opcional — podés asignar después">
                                    <MenuItem value="">— Sin asignar —</MenuItem>
                                    {controladores.map(c => (
                                        <MenuItem key={c.username} value={c.username}>
                                            <Box>
                                                <Typography variant="body2">{c.nombre_completo}</Typography>
                                                {c.sucursal && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {c.sucursal}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth label="Creado por" value={username}
                                    disabled helperText="Auditor logueado"
                                />
                            </Stack>
                        </Box>
                    </Card>

                    {/* Acciones */}
                    <Stack gap={1}>
                        <Button
                            fullWidth variant="contained"
                            startIcon={guardando
                                ? <CircularProgress size={14} color="inherit" />
                                : <SaveIcon />}
                            onClick={handleSubmit}
                            disabled={guardando}
                            sx={{ py: 1 }}
                        >
                            {guardando ? "Guardando..." : "Guardar ítem"}
                        </Button>
                        <Button
                            fullWidth variant="outlined" color="inherit"
                            onClick={() => navigate(-1)} disabled={guardando}
                        >
                            Cancelar
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}